import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useUserPersona } from '../context/UserPersonaContext';
import registrationService from '../services/registrationService';
import { getApiBaseUrlWithFallback, API_BASE_URL } from '../config/environment';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Collapse,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  School as CoachIcon,
  Visibility as FollowIcon,
  Business as ClubIcon,
  Public as InterclubIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  PauseCircle as PauseIcon,
  ExitToApp as WithdrawIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { fetchInterclubs, fetchClubs } from '../services/clubService';
import usfsService from '../services/usfsService';
import USFSLinkDialog from '../components/USFSLinkDialog';
import WidgetCard from '../components/WidgetCard';
import {
  mergeAdultIcmLevelsIntoProfileOptions,
  ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE,
} from '../utils/skatingLevels';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';

export default function Skaters() {
  const navigate = useNavigate();
  const { user, getIdToken, loading: authLoading } = useFirebaseAuth();
  const { showQualifierLink } = useAuth();
  const { getNavigationLabels } = useUserPersona();

  // Get dynamic labels based on persona
  const navLabels = getNavigationLabels();

  // State for interclub-specific supported levels
  const [supportedLevels, setSupportedLevels] = useState(null);

  const levelOptions = useMemo(() => {
    if (!supportedLevels?.length) return ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE;
    const filtered = ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE.filter((level) =>
      supportedLevels.includes(level)
    );
    return mergeAdultIcmLevelsIntoProfileOptions(filtered, ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE);
  }, [supportedLevels]);

  const [skaters, setSkaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [currentSeasonId, setCurrentSeasonId] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [selectedSeasonCompetitionNames, setSelectedSeasonCompetitionNames] = useState([]);
  const [selectedSeasonCompetitions, setSelectedSeasonCompetitions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [seasonInfo, setSeasonInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [interclubInfo, setInterclubInfo] = useState(null);
  const [seasonFee, setSeasonFee] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);
  const [paymentConfigMissing, setPaymentConfigMissing] = useState(false);
  const [hasManualSeasonSelection, setHasManualSeasonSelection] = useState(false);
  const [profile, setProfile] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [coachedSkaters, setCoachedSkaters] = useState([]);
  const [followedSkaters, setFollowedSkaters] = useState([]);
  const [clubSkaters, setClubSkaters] = useState([]);
  const [interclubSkaters, setInterclubSkaters] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState({});
  const [followSkaterDialogOpen, setFollowSkaterDialogOpen] = useState(false);
  const [followSkaterInput, setFollowSkaterInput] = useState('');
  const [followSkaterLoading, setFollowSkaterLoading] = useState(false);
  const [followSkaterError, setFollowSkaterError] = useState('');
  const [linkSkaterDialogOpen, setLinkSkaterDialogOpen] = useState(false);
  const [linkSkaterInput, setLinkSkaterInput] = useState('');
  const [linkSkaterLoading, setLinkSkaterLoading] = useState(false);
  const [linkSkaterError, setLinkSkaterError] = useState('');

  // USFS account linking state (after adding a new skater)
  const [usfsLinkPromptOpen, setUsfsLinkPromptOpen] = useState(false);
  const [usfsLinkDialogOpen, setUsfsLinkDialogOpen] = useState(false);
  const [usfsLinkSkater, setUsfsLinkSkater] = useState(null);
  const [usfsLinkLoading, setUsfsLinkLoading] = useState(false);
  const [usfsLinkError, setUsfsLinkError] = useState('');
  const [pendingUsfsCredentials, setPendingUsfsCredentials] = useState(null); // Credentials from import to auto-link after save
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreError, setScoreError] = useState(null);
  const [expandedSkaters, setExpandedSkaters] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSkater, setEditingSkater] = useState(null);
  const [prePrelimAcknowledged, setPrePrelimAcknowledged] = useState(false);
  const [skaterForm, setSkaterForm] = useState({
    firstName: '',
    lastName: '',
    membershipId: '', // USFS ID - required
    level: '',
    birthDate: '',
    interclubId: '',
    clubId: '',
    club: '',
    coaches: [], // Array of { coachId, displayName, email }
    notes: ''
  });

  const loadSkaters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      // Load basic data first
      const [skatersData, coachedData, profileData, coachesData] = await Promise.all([
        registrationService.getSkaters(token),
        registrationService.getCoachedSkaters(token),
        registrationService.getProfile(token),
        registrationService.getCoaches(token, { usfsOnly: true }),
      ]);
      setSkaters(skatersData);
      setCoachedSkaters(coachedData);
      setProfile(profileData);
      setCoaches(coachesData);

      // Get user roles and approval status
      const roles = profileData?.roles || [];
      const approvals = profileData?.approvalStatus || {};
      setUserRoles(roles);
      setApprovalStatus(approvals);

      // Load role-specific data
      const loadPromises = [];

      // Load followed skaters if user is an approved coach
      if (roles.includes('coach') && approvals.coach === 'approved') {
        loadPromises.push(
          registrationService.getFollowedSkaters(token).then(setFollowedSkaters).catch(() => setFollowedSkaters([]))
        );
      }

      // Load club skaters if user is an approved club admin
      if (roles.includes('club_admin') && approvals.clubAdmin === 'approved') {
        loadPromises.push(
          registrationService.getClubSkaters(token).then(setClubSkaters).catch(() => setClubSkaters([]))
        );
      }

      // Load interclub skaters if user is an approved interclub admin
      if (roles.includes('interclub_admin') && approvals.interclubAdmin === 'approved') {
        loadPromises.push(
          registrationService.getInterclubSkaters(token).then(setInterclubSkaters).catch(() => setInterclubSkaters([]))
        );
      }

      await Promise.all(loadPromises);
    } catch (err) {
      setError('Failed to load skaters');
      console.error('Error loading skaters:', err);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  const loadInterclubs = useCallback(async () => {
    try {
      const interclubList = await fetchInterclubs();
      setInterclubs(interclubList);
    } catch (err) {
      console.error('Error loading interclubs:', err);
    }
  }, []);

  const loadClubs = useCallback(async (interclubId = '') => {
    try {
      const clubList = await fetchClubs(interclubId);
      setClubs(clubList);
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  }, []);

  useEffect(() => {
    // Don't redirect if auth is still loading
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate('/');
      return;
    }
    loadSkaters();
    loadInterclubs();
  }, [user, navigate, loadSkaters, loadInterclubs, authLoading]);

  useEffect(() => {
    const loadSeasons = async () => {
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const response = await fetch(`${baseUrl}/seasons`);
        if (!response.ok) {
          return;
        }
        const seasonsData = await response.json();
        setSeasons(seasonsData);

        const currentSeasons = seasonsData.filter((season) => season.isCurrent);
        const defaultSeasonId = currentSeasons[0]?.id || seasonsData[0]?.id || '';
        setCurrentSeasonId(currentSeasons[0]?.id || '');
        if (!selectedSeasonId) {
          setSelectedSeasonId(defaultSeasonId);
        }
      } catch (err) {
        console.error('Error loading seasons:', err);
      }
    };
    loadSeasons();
  }, [selectedSeasonId]);

  useEffect(() => {
    if (hasManualSeasonSelection || seasons.length === 0) return;
    const currentSeasons = seasons.filter((season) => season.isCurrent);
    const skaterInterclubIds = new Set(skaters.map((skater) => skater.interclubId).filter(Boolean));
    let preferredSeason = null;
    if (skaterInterclubIds.size > 0) {
      preferredSeason = currentSeasons.find((season) => {
        if (skaterInterclubIds.has(season.interclubId)) return true;
        return Array.isArray(season.interclubIds) && season.interclubIds.some((id) => skaterInterclubIds.has(id));
      });
    }
    if (!preferredSeason) {
      preferredSeason = currentSeasons[0] || seasons[0];
    }
    if (preferredSeason?.id) {
      setSelectedSeasonId(preferredSeason.id);
      if (preferredSeason.isCurrent) {
        setCurrentSeasonId(preferredSeason.id);
      }
    }
  }, [hasManualSeasonSelection, seasons, skaters]);

  useEffect(() => {
    const loadSeasonCompetitions = async () => {
      if (!selectedSeasonId) {
        setSelectedSeasonCompetitionNames([]);
        return;
      }
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const competitionsResponse = await fetch(`${baseUrl}/seasons/${selectedSeasonId}/competitions`);
        if (competitionsResponse.ok) {
          const competitions = await competitionsResponse.json();
          const names = competitions.map((competition) => competition.name).filter(Boolean);
          setSelectedSeasonCompetitionNames(names);
          setSelectedSeasonCompetitions(competitions);
        }
      } catch (err) {
        console.error('Error loading season competitions:', err);
      }
    };
    loadSeasonCompetitions();
  }, [selectedSeasonId]);

  useEffect(() => {
    const loadSeasonPaymentStatus = async () => {
      if (!selectedSeasonId) {
        setSeasonInfo(null);
        setInterclubInfo(null);
        setSeasonFee(null);
        setRegistrations([]);
        setPaymentConfigMissing(false);
        setRegistrationsLoading(false);
        return;
      }
      setRegistrationsLoading(true);
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const seasonResponse = await fetch(`${baseUrl}/seasons/${selectedSeasonId}`);
        if (!seasonResponse.ok) {
          setRegistrationsLoading(false);
          return;
        }
        const season = await seasonResponse.json();
        setSeasonInfo(season);

        let interclub = null;
        if (season.interclubId) {
          const interclubResponse = await fetch(`${baseUrl}/interclubs/${season.interclubId}`);
          if (interclubResponse.ok) {
            interclub = await interclubResponse.json();
            setInterclubInfo(interclub);
            // Set supported levels from interclub config
            if (interclub.supportedLevels && interclub.supportedLevels.length > 0) {
              setSupportedLevels(interclub.supportedLevels);
            } else {
              setSupportedLevels(null); // Fall back to all levels
            }
          }
        }

        const feeValue = season.seasonEntryFee ?? interclub?.seasonEntryFee ?? null;
        setSeasonFee(feeValue);
        const missingPaymentConfig = !interclub?.stripeAccountId || !feeValue;
        setPaymentConfigMissing(missingPaymentConfig);

        const token = await getIdToken(true);
        if (token) {
          const skaterIds = skaters.map((skater) => skater.id).filter(Boolean);
          const registrationData = await registrationService.getSeasonRegistrations(selectedSeasonId, token, skaterIds);
          setRegistrations(registrationData);
        }
      } catch (err) {
        console.error('Error loading season payment status:', err);
      } finally {
        setRegistrationsLoading(false);
      }
    };
    loadSeasonPaymentStatus();
  }, [selectedSeasonId, getIdToken, skaters]);

  const calculateTotalScore = useCallback((data) => {
    const sortedScores = data.competitions.map((comp) => comp.points).sort((a, b) => b - a);
    return sortedScores.slice(0, 3).reduce((sum, score) => sum + score, 0);
  }, []);

  const formatUSFSTimestamp = useCallback((value) => {
    if (!value) return 'Not synced yet';
    if (typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString();
    }
    if (typeof value._seconds === 'number') {
      return new Date(value._seconds * 1000).toLocaleDateString();
    }
    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString();
    }
    return 'Not synced yet';
  }, []);

  const getQualificationStatus = useCallback((data) => {
    if (!showQualifierLink) {
      return null;
    }
    const totalScore = calculateTotalScore(data);
    const competitionCount = data.competitions.length;
    if (competitionCount < 3) {
      return {
        status: 'incomplete',
        message: `Needs ${3 - competitionCount} more competition${3 - competitionCount === 1 ? '' : 's'}`,
        color: 'warning.main',
        bgColor: 'warning.50',
      };
    }
    if (totalScore >= 15) {
      return {
        status: 'likely',
        message: 'Likely to qualify for championship',
        color: 'success.main',
        bgColor: 'success.50',
      };
    }
    if (totalScore >= 10) {
      return {
        status: 'borderline',
        message: 'Borderline qualification status',
        color: 'info.main',
        bgColor: 'info.50',
      };
    }
    return {
      status: 'unlikely',
      message: 'Unlikely to qualify with current scores',
      color: 'warning.main',
      bgColor: 'warning.50',
    };
  }, [calculateTotalScore, showQualifierLink]);

  const handleAddSkater = () => {
    setEditingSkater(null);
    setPrePrelimAcknowledged(false);
    setPendingUsfsCredentials(null); // Clear any pending import credentials
    setSkaterForm({
      firstName: '',
      lastName: '',
      membershipId: '',
      level: '',
      birthDate: '',
      interclubId: '',
      clubId: '',
      club: '',
      coaches: [],
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleEditSkater = (skater) => {
    setEditingSkater(skater);
    if (skater.interclubId) {
      loadClubs(skater.interclubId);
    }
    const seasonLevels = currentSeasonId ? skater.seasonLevels?.[currentSeasonId] : null;
    // Convert legacy single coach to coaches array if needed
    let skaterCoaches = skater.coaches || [];
    if (skaterCoaches.length === 0 && (skater.coachName || skater.coachUserId)) {
      skaterCoaches = [{
        coachId: skater.coachUserId || '',
        displayName: skater.coachName || '',
        email: skater.coachEmail || '',
      }];
    }

    const currentLevel = seasonLevels?.currentLevel || seasonLevels?.startingLevel || skater.currentLevel || skater.startingLevel || skater.level || '';
    // Only pre-check if they have an actual acknowledgment on file
    // Don't assume - require explicit acknowledgment if not tracked
    setPrePrelimAcknowledged(!!skater.prePrelimAcknowledgedAt);

    // Convert birthDate to YYYY-MM-DD format for the date input
    let formattedBirthDate = '';
    const rawBirthDate = skater.birthDate || skater.dateOfBirth;
    if (rawBirthDate) {
      if (typeof rawBirthDate === 'string') {
        // Check if already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(rawBirthDate)) {
          formattedBirthDate = rawBirthDate;
        } else if (rawBirthDate.includes('/')) {
          // M/D/YYYY format
          const parts = rawBirthDate.split('/');
          if (parts.length === 3) {
            formattedBirthDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
      } else if (rawBirthDate._seconds) {
        // Firestore timestamp - use UTC to avoid timezone shift
        const date = new Date(rawBirthDate._seconds * 1000);
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        formattedBirthDate = `${y}-${m}-${d}`;
      }
    }

    setSkaterForm({
      firstName: skater.firstName || '',
      lastName: skater.lastName || '',
      membershipId: skater.membershipId || '',
      level: currentLevel,
      birthDate: formattedBirthDate,
      interclubId: skater.interclubId || '',
      clubId: skater.clubId || '',
      club: skater.club || '',
      coaches: skaterCoaches,
      notes: skater.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSaveSkater = async () => {
    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const existingSeasonLevels = currentSeasonId ? editingSkater?.seasonLevels?.[currentSeasonId] : null;
      const resolvedStartingLevel = existingSeasonLevels?.startingLevel
        || editingSkater?.startingLevel
        || editingSkater?.currentLevel
        || skaterForm.startingLevel
        || skaterForm.level
        || '';
      // Extract primary coach for legacy field compatibility
      const primaryCoach = skaterForm.coaches?.[0] || {};

      const payload = {
        ...skaterForm,
        // Map birthDate to dateOfBirth for backend compatibility
        dateOfBirth: skaterForm.birthDate || skaterForm.dateOfBirth || '',
        currentLevel: skaterForm.level || skaterForm.currentLevel || '',
        startingLevel: resolvedStartingLevel,
        seasonId: currentSeasonId || '',
        // Legacy coach fields for backward compatibility
        coachName: primaryCoach.displayName || '',
        coachUserId: primaryCoach.coachId || '',
        coachEmail: primaryCoach.email || '',
        // Pre-Preliminary acknowledgment tracking for audit purposes
        // Only add new acknowledgment if not already on file
        ...(skaterForm.level === 'Pre-Preliminary' && prePrelimAcknowledged && !editingSkater?.prePrelimAcknowledgedAt ? {
          prePrelimAcknowledgedAt: new Date().toISOString(),
          prePrelimAcknowledgedBy: user?.uid || null,
          prePrelimAcknowledgedByEmail: user?.email || null,
        } : {}),
      };

      if (editingSkater) {
        // Update existing skater
        await registrationService.updateSkater(editingSkater.id, payload, token);
        setDialogOpen(false);
        loadSkaters();
      } else {
        // Create new skater
        const response = await registrationService.createSkater(payload, token);
        const newSkater = response?.skater || response; // Handle both { skater: {...} } and direct object
        setDialogOpen(false);
        await loadSkaters();

        // If we have pending USFS credentials from import, auto-link the account
        const skaterId = newSkater?.id;
        console.log('After createSkater - skaterId:', skaterId);
        console.log('After createSkater - pendingUsfsCredentials:', pendingUsfsCredentials ? 'present' : 'null');
        
        if (skaterId && pendingUsfsCredentials) {
          console.log('Attempting to auto-link USFS account for skater:', skaterId);
          try {
            const linkResult = await usfsService.linkSkaterAccount(
              skaterId,
              { ...pendingUsfsCredentials, consent: true },
              token
            );
            console.log('USFS link result:', linkResult);
            setSuccess(`${skaterForm.firstName} added and USFS account linked successfully!`);
            setPendingUsfsCredentials(null);
            setTimeout(() => setSuccess(null), 5000);
            loadSkaters(); // Reload to show verified status
          } catch (linkErr) {
            console.error('Auto-link USFS failed:', linkErr);
            setSuccess(`${skaterForm.firstName} added! USFS link failed - you can link later from skater details.`);
            setPendingUsfsCredentials(null);
            setTimeout(() => setSuccess(null), 5000);
          }
        } else if (skaterId) {
          console.log('No pending credentials - showing link prompt');
          console.log('skaterId:', skaterId);
          console.log('pendingUsfsCredentials:', pendingUsfsCredentials);
          // No import credentials - prompt to link USFS account
          setUsfsLinkSkater({
            id: skaterId,
            firstName: skaterForm.firstName,
            lastName: skaterForm.lastName,
            membershipId: skaterForm.membershipId,
          });
          setUsfsLinkPromptOpen(true);
        }
      }
    } catch (err) {
      setError('Failed to save skater');
      console.error('Error saving skater:', err);
    }
  };

  // USFS Link handlers
  const handleUsfsLinkPromptYes = () => {
    setUsfsLinkPromptOpen(false);
    setUsfsLinkError('');
    setUsfsLinkDialogOpen(true);
  };

  const handleUsfsLinkPromptSkip = () => {
    setUsfsLinkPromptOpen(false);
    setUsfsLinkSkater(null);
    setSuccess(`${usfsLinkSkater?.firstName} added successfully! You can link their USFS account later from the skater details page.`);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleUsfsLinkSubmit = async ({ username, password, consent }) => {
    try {
      setUsfsLinkLoading(true);
      setUsfsLinkError('');
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      // For new skaters, lookup USFS data to auto-fill the form
      if (usfsLinkSkater?.isNewSkater) {
        const result = await usfsService.lookupMember({ username, password }, token);
        console.log('USFS lookup result:', result);

        if (result.success && result.member) {
          const member = result.member;
          console.log('USFS member data:', member);
          console.log('USFS club from response:', member.club);

          // Convert birthDate to YYYY-MM-DD format if needed
          let formattedBirthDate = member.birthDate || '';
          console.log('USFS birthDate from response:', member.birthDate);
          if (formattedBirthDate) {
            // Handle different date formats:
            // - "2010-09-25T00:00:00" (timestamp)
            // - "9/25/2010" (M/D/YYYY)
            // - "2010-09-25" (already YYYY-MM-DD)
            
            // Check if it's already in YYYY-MM-DD format
            if (/^\d{4}-\d{2}-\d{2}$/.test(formattedBirthDate)) {
              // Already in correct format
            } else if (formattedBirthDate.includes('T')) {
              // ISO timestamp format - extract date part
              formattedBirthDate = formattedBirthDate.split('T')[0];
            } else if (formattedBirthDate.includes('/')) {
              // M/D/YYYY format - parse and convert
              const parts = formattedBirthDate.split('/');
              if (parts.length === 3) {
                const month = parts[0].padStart(2, '0');
                const day = parts[1].padStart(2, '0');
                const year = parts[2];
                formattedBirthDate = `${year}-${month}-${day}`;
              }
            } else {
              // Try generic Date parsing as fallback
              const dateObj = new Date(formattedBirthDate);
              if (!isNaN(dateObj.getTime())) {
                formattedBirthDate = dateObj.toISOString().split('T')[0];
              }
            }
          }
          console.log('Formatted birthDate:', formattedBirthDate);

          // Try to match the USFS club to a local club
          let matchedClubId = '';
          let matchedInterclubId = '';
          let matchedClubName = '';

          if (member.club) {
            // Fetch all clubs to find a match
            try {
              const allClubs = await fetchClubs(); // Get all clubs
              console.log('All clubs fetched:', allClubs.length, 'clubs');
              console.log('Sample clubs:', allClubs.slice(0, 3).map(c => ({ name: c.name, aliases: c.aliases, interclubId: c.interclubId })));
              const usfsClubLower = member.club.toLowerCase().trim();
              console.log('Looking for club match:', usfsClubLower);

              // Normalize common abbreviations for matching
              const normalizeForMatch = (str) => {
                if (!str) return '';
                return str
                  .toLowerCase()
                  .trim()
                  // Normalize LTS variations
                  .replace(/learn to skate/gi, 'lts')
                  .replace(/figure skating club/gi, 'fsc')
                  .replace(/skating club/gi, 'sc')
                  .replace(/ice arena/gi, 'ia')
                  // Remove common filler words
                  .replace(/\s+/g, ' ');
              };

              // Helper function to check if two strings match
              const matchesName = (name1, name2) => {
                if (!name1 || !name2) return false;
                const n1 = name1.toLowerCase().trim();
                const n2 = name2.toLowerCase().trim();
                // Direct match
                if (n1 === n2 || n1.includes(n2) || n2.includes(n1)) {
                  return true;
                }
                // Normalized match (LTS = Learn to Skate, etc.)
                const norm1 = normalizeForMatch(n1);
                const norm2 = normalizeForMatch(n2);
                return norm1 === norm2 || norm1.includes(norm2) || norm2.includes(norm1);
              };

              // Try to find a matching club by name OR aliases
              const matchedClub = allClubs.find(club => {
                // Check main club name
                if (matchesName(club.name, usfsClubLower)) {
                  console.log('Matched by club name:', club.name);
                  return true;
                }
                // Check short name
                if (matchesName(club.shortName, usfsClubLower)) {
                  console.log('Matched by short name:', club.shortName);
                  return true;
                }
                // Check aliases array
                if (club.aliases && Array.isArray(club.aliases)) {
                  const aliasMatch = club.aliases.some(alias => matchesName(alias, usfsClubLower));
                  if (aliasMatch) {
                    console.log('Matched by alias in club:', club.name, 'aliases:', club.aliases);
                  }
                  return aliasMatch;
                }
                return false;
              });

              console.log('Matched club:', matchedClub);

              if (matchedClub) {
                matchedClubId = matchedClub.id;
                matchedInterclubId = matchedClub.interclubId || '';
                matchedClubName = matchedClub.name;
                console.log('Setting club:', matchedClubId, matchedClubName, 'interclub:', matchedInterclubId);
                // Load clubs for the interclub so the dropdown is populated
                if (matchedInterclubId) {
                  await loadClubs(matchedInterclubId);
                  console.log('Loaded clubs for interclub:', matchedInterclubId);
                }
              } else {
                console.log('No club match found for:', usfsClubLower);
              }
            } catch (clubErr) {
              console.log('Could not fetch clubs for matching:', clubErr);
            }
          } else {
            console.log('No club in USFS response');
          }

          // Auto-fill the skater form with USFS data
          const newFormValues = {
            firstName: member.firstName,
            lastName: member.lastName,
            membershipId: member.memberId || member.membershipId || username,
            birthDate: formattedBirthDate,
            clubId: matchedClubId,
            interclubId: matchedInterclubId,
            club: matchedClubName || member.club,
          };
          console.log('Setting form values:', newFormValues);
          
          setSkaterForm(prev => ({
            ...prev,
            firstName: newFormValues.firstName || prev.firstName,
            lastName: newFormValues.lastName || prev.lastName,
            membershipId: newFormValues.membershipId || prev.membershipId,
            birthDate: newFormValues.birthDate || prev.birthDate,
            clubId: newFormValues.clubId || prev.clubId,
            interclubId: newFormValues.interclubId || prev.interclubId,
            club: newFormValues.club || prev.club,
          }));
          
          // Save credentials to auto-link after skater is created
          console.log('Saving USFS credentials for auto-link after save');
          setPendingUsfsCredentials({ username, password });

          setUsfsLinkDialogOpen(false);

          const clubMessage = matchedClubId
            ? `Club matched: ${matchedClubName}`
            : member.club
              ? `Club "${member.club}" not found in system - please select manually`
              : '';
          setSuccess(`USFS data loaded! ${clubMessage}`.trim());
          setTimeout(() => setSuccess(null), 6000);
        } else {
          throw new Error(result.error || 'Could not find member data');
        }
        setUsfsLinkSkater(null);
        return;
      }

      // For existing skaters, link the account
      if (!usfsLinkSkater?.id) return;

      await usfsService.linkSkaterAccount(usfsLinkSkater.id, { username, password, consent }, token);
      setUsfsLinkDialogOpen(false);
      setSuccess(`${usfsLinkSkater.firstName}'s USFS account linked successfully!`);
      setUsfsLinkSkater(null);
      setTimeout(() => setSuccess(null), 5000);
      loadSkaters(); // Reload to show verified status
    } catch (err) {
      console.error('USFS link error:', err);
      setUsfsLinkError(err.message || 'Failed to link USFS account. Please check the credentials and try again.');
    } finally {
      setUsfsLinkLoading(false);
    }
  };

  const handleUsfsLinkDialogClose = () => {
    setUsfsLinkDialogOpen(false);
    if (usfsLinkSkater) {
      setSuccess(`${usfsLinkSkater.firstName} added successfully! You can link their USFS account later.`);
      setTimeout(() => setSuccess(null), 5000);
    }
    setUsfsLinkSkater(null);
    setUsfsLinkError('');
  };

  const handleDeleteSkater = async (skaterId) => {
    if (!window.confirm('Are you sure you want to delete this skater? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      await registrationService.deleteSkater(skaterId, token);
      setError(null);
      await loadSkaters();
      setSuccess('Skater deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete skater');
      console.error('Error deleting skater:', err);
    }
  };

  const handleInterclubChange = async (event) => {
    const interclubId = event.target.value;
    setSkaterForm((prev) => ({
      ...prev,
      interclubId,
      clubId: '',
      club: '',
    }));
    if (interclubId) {
      await loadClubs(interclubId);
    } else {
      setClubs([]);
    }
  };

  const handleClubChange = (event) => {
    const clubId = event.target.value;
    const selectedClub = clubs.find((club) => club.id === clubId);
    setSkaterForm((prev) => ({
      ...prev,
      clubId,
      club: selectedClub?.name || '',
      interclubId: selectedClub?.interclubId || prev.interclubId,
    }));
  };

  // Follow a skater by USFS membership ID
  const handleFollowSkater = async () => {
    if (!followSkaterInput.trim()) {
      setFollowSkaterError('Please enter a USFS membership ID');
      return;
    }

    setFollowSkaterLoading(true);
    setFollowSkaterError('');

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const result = await registrationService.followSkater(followSkaterInput.trim(), token);
      setFollowedSkaters((prev) => [...prev, result.skater]);
      setFollowSkaterDialogOpen(false);
      setFollowSkaterInput('');
      setSuccess(`Now following ${result.skater.firstName} ${result.skater.lastName}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setFollowSkaterError(err.message || 'Failed to follow skater');
    } finally {
      setFollowSkaterLoading(false);
    }
  };

  // Unfollow a skater
  const handleUnfollowSkater = async (skaterId, skaterName) => {
    if (!window.confirm(`Are you sure you want to stop following ${skaterName}?`)) {
      return;
    }

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      await registrationService.unfollowSkater(skaterId, token);
      setFollowedSkaters((prev) => prev.filter((s) => s.id !== skaterId));
      setSuccess(`Stopped following ${skaterName}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to unfollow skater');
      console.error('Error unfollowing skater:', err);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setEditingSkater(null);
    setPendingUsfsCredentials(null); // Clear any pending import credentials
    setSkaterForm({
      firstName: '',
      lastName: '',
      level: '',
      birthDate: '',
      interclubId: '',
      clubId: '',
      club: '',
      coaches: [],
      notes: ''
    });
  };

  // Link an existing skater to the current coach by USFS ID
  const handleLinkSkater = async () => {
    if (!linkSkaterInput.trim()) {
      setLinkSkaterError('Please enter a USFS membership ID');
      return;
    }

    setLinkSkaterLoading(true);
    setLinkSkaterError('');

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const result = await registrationService.linkSkaterToCoach(linkSkaterInput.trim(), token);
      // The skater should now appear in coached skaters
      setCoachedSkaters((prev) => {
        // Avoid duplicates
        if (prev.some((s) => s.id === result.skater.id)) {
          return prev;
        }
        return [...prev, result.skater];
      });
      setLinkSkaterDialogOpen(false);
      setLinkSkaterInput('');
      setSuccess(`${result.skater.firstName} ${result.skater.lastName} has been linked to your account`);
      setTimeout(() => setSuccess(null), 3000);
      // Reload to refresh coached skaters
      loadSkaters();
    } catch (err) {
      setLinkSkaterError(err.message || 'Failed to link skater');
    } finally {
      setLinkSkaterLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  const getSeasonLevels = (skater) => {
    if (!selectedSeasonId) {
      return {
        startingLevel: skater.startingLevel,
        currentLevel: skater.currentLevel,
        levelChanged: false,
      };
    }
    const seasonLevels = skater.seasonLevels?.[selectedSeasonId];
    return {
      startingLevel: seasonLevels?.startingLevel || skater.startingLevel,
      currentLevel: seasonLevels?.currentLevel || skater.currentLevel,
      levelChanged: seasonLevels?.levelChanged || false,
    };
  };

  const selectedSeason = seasons.find((season) => season.id === selectedSeasonId);
  const selectedSeasonName = selectedSeason?.name || '';
  const selectedSeasonYear = (() => {
    const endValue = selectedSeason?.endDate;
    if (endValue?._seconds) {
      return new Date(endValue._seconds * 1000).getFullYear();
    }
    if (endValue?.toDate) {
      return endValue.toDate().getFullYear();
    }
    if (endValue) {
      const parsed = new Date(endValue);
      if (!Number.isNaN(parsed.getTime())) return parsed.getFullYear();
    }
    const startValue = selectedSeason?.startDate;
    if (startValue?._seconds) {
      return new Date(startValue._seconds * 1000).getFullYear();
    }
    if (startValue?.toDate) {
      return startValue.toDate().getFullYear();
    }
    if (startValue) {
      const parsed = new Date(startValue);
      if (!Number.isNaN(parsed.getTime())) return parsed.getFullYear();
    }
    const nameMatch = selectedSeasonName.match(/(\d{4})\D+(\d{4})/);
    if (nameMatch) {
      return parseInt(nameMatch[2], 10);
    }
    return null;
  })();
  // Check both the isCoach toggle AND the roles array (from role approval system)
  const isCoach = Boolean(profile?.isCoach) || Boolean(profile?.roles?.includes('coach'));
  // A user is considered a "parent" if they have their own skaters (not just coached ones)
  // or if their profile indicates parent role or they're not exclusively a coach
  const isParent = Boolean(
    skaters.length > 0 || // Has their own skaters
    profile?.isParent || // Explicitly marked as parent
    !isCoach || // Not a coach, so default to parent flow
    (isCoach && (profile?.isParent || profile?.isSkater)) // Coach who is also parent/skater
  );
  // Coach-only: is a coach but has no skaters and didn't mark themselves as parent/skater
  const isCoachOnly = Boolean(
    isCoach &&
    !profile?.isParent &&
    !profile?.isSkater &&
    skaters.length === 0
  );
  const mySkaters = skaters;
  // Coached skaters: only those whose profile lists current user as coach (from API)
  const myCoachedSkaters = coachedSkaters;
  const paidSkaterIds = new Set(registrations.map((registration) => registration.skaterId));
  const unregisteredSkaters = mySkaters.filter((skater) => !paidSkaterIds.has(skater.id));
  const canRegisterSeason =
    selectedSeasonId &&
    selectedSeasonId === currentSeasonId &&
    !paymentConfigMissing &&
    seasonFee !== null &&
    isSeasonRegistrationOpen(selectedSeason);
  const showRegistrationPrompt = canRegisterSeason && unregisteredSkaters.length > 0;
  // Skaters at Pre-Preliminary level without eligibility acknowledgment on file (must complete before season registration)
  const prePrelimNeedsAck = mySkaters.filter(
    (s) =>
      (s.currentLevel === 'Pre-Preliminary' || s.startingLevel === 'Pre-Preliminary') &&
      !s.prePrelimAcknowledgedAt
  );
  const showPrePrelimAckNotice = prePrelimNeedsAck.length > 0;

  const handleViewResults = async (skater) => {
    if (!skater?.membershipId && !skater?.id) {
      setScoreError('Missing USFS ID or skater for this view.');
      return;
    }
    setSelectedSkater(skater);
    setScoreError(null);
    setScoreData(null);
    setScoreLoading(true);
    try {
      let data = null;
      let usedEligibilityApi = false;
      // Prefer eligibility API (same source as qualifiers/interclub) when we have skater.id and a season
      if (skater.id && selectedSeasonId) {
        const baseUrl = await getApiBaseUrlWithFallback();
        const token = await getIdToken(true);
        if (token) {
          const eligRes = await fetch(
            `${baseUrl}/skaters/${skater.id}/eligibility?seasonId=${encodeURIComponent(selectedSeasonId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (eligRes.ok) {
            const elig = await eligRes.json();
            const competitions = (elig.competitionsAttended || [])
              .filter((c) => c.counts)
              .map((c) => ({
                competitionName: c.competitionName || 'Unknown',
                points: c.points || 0,
                level: c.level || '',
                date: c.date || null,
              }));
            const firstResultLevel = competitions.find((c) => c.level)?.level || '';
            const lastResultLevel = [...competitions].reverse().find((c) => c.level)?.level || firstResultLevel;
            data = {
              membershipId: skater.membershipId || '',
              name: elig.skaterName || `${skater.firstName || ''} ${skater.lastName || ''}`.trim(),
              skatingClub: elig.skaterClubName || skater.club || skater.clubName || 'Unknown',
              startingCompetitionLevel: firstResultLevel,
              competitionLevel: lastResultLevel,
              competitions,
            };
            usedEligibilityApi = true;
          }
        }
      }
      if (!data) {
        const response = await fetch(`${API_BASE_URL}/get-score/${skater.membershipId}`);
        if (!response.ok) {
          throw new Error('Unable to load results');
        }
        data = await response.json();
      }

      if (!usedEligibilityApi) {
        const normalizeName = (value) => (value || '').toLowerCase().replace(/^\d{4}\s+/, '').trim();
        const nameSet = new Set(selectedSeasonCompetitionNames.map((name) => normalizeName(name)));
        const seasonCompetitionMap = new Map(
          selectedSeasonCompetitions
            .filter((competition) => competition.name)
            .map((competition) => [normalizeName(competition.name), competition])
        );
        if (nameSet.size > 0) {
          data.competitions = (data.competitions || []).filter((comp) => {
            const rawName = (comp.competitionName || '').trim();
            const normalized = normalizeName(rawName);
            if (!nameSet.has(normalized)) {
              return false;
            }
            const match = seasonCompetitionMap.get(normalized);
            if (!match) return false;
            const yearMatch = rawName.match(/^(\d{4})\s+/);
            if (yearMatch) {
              const compYear = parseInt(yearMatch[1], 10);
              const startValue = match.startDate || match.date;
              let seasonYear = null;
              if (startValue?._seconds) {
                seasonYear = new Date(startValue._seconds * 1000).getFullYear();
              } else if (startValue?.toDate) {
                seasonYear = startValue.toDate().getFullYear();
              } else if (startValue) {
                const parsed = new Date(startValue);
                seasonYear = Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
              }
              if (seasonYear && compYear !== seasonYear) {
                return false;
              }
            }
            return true;
          });
        } else if (selectedSeasonYear) {
          data.competitions = (data.competitions || []).filter((comp) => {
            const rawName = (comp.competitionName || '').trim();
            const yearMatch = rawName.match(/^(\d{4})\s+/);
            if (!yearMatch) return true;
            return parseInt(yearMatch[1], 10) === selectedSeasonYear;
          });
        }
      }

      const competitionStartLevel = data.startingCompetitionLevel || data.competitionLevel || '';
      const competitionCurrentLevel = data.competitionLevel || data.startingCompetitionLevel || '';

      if (!usedEligibilityApi) {
        const hasExistingSeasonLevels = skater.seasonLevels &&
          skater.seasonLevels[selectedSeasonId] &&
          (skater.seasonLevels[selectedSeasonId].startingLevel ||
            skater.seasonLevels[selectedSeasonId].currentLevel);

        if (selectedSeasonId && skater.id && competitionStartLevel && competitionCurrentLevel && !hasExistingSeasonLevels) {
          const levelChanged = competitionStartLevel !== competitionCurrentLevel;
          try {
            const token = await getIdToken(true);
            if (token) {
              await registrationService.updateSkater(
                skater.id,
                {
                  seasonId: selectedSeasonId,
                  startingLevel: competitionStartLevel,
                  currentLevel: competitionCurrentLevel,
                  levelChanged,
                },
                token
              );
              setSkaters((prev) =>
                prev.map((entry) => {
                  if (entry.id !== skater.id) return entry;
                  return {
                    ...entry,
                    seasonLevels: {
                      ...(entry.seasonLevels || {}),
                      [selectedSeasonId]: {
                        ...(entry.seasonLevels?.[selectedSeasonId] || {}),
                        startingLevel: competitionStartLevel,
                        currentLevel: competitionCurrentLevel,
                        levelChanged,
                      },
                    },
                  };
                })
              );
              setSelectedSkater((prev) => {
                if (!prev || prev.id !== skater.id) return prev;
                return {
                  ...prev,
                  seasonLevels: {
                    ...(prev.seasonLevels || {}),
                    [selectedSeasonId]: {
                      ...(prev.seasonLevels?.[selectedSeasonId] || {}),
                      startingLevel: competitionStartLevel,
                      currentLevel: competitionCurrentLevel,
                      levelChanged,
                    },
                  },
                };
              });
            }
          } catch (syncError) {
            console.error('Failed to sync competition levels:', syncError);
          }
        }
      }
      setScoreData(data);
    } catch (error) {
      console.error('Error loading score data:', error);
      setScoreError('Failed to load competition results.');
    } finally {
      setScoreLoading(false);
    }
  };

  const toggleSkaterExpanded = (skaterId) => {
    setExpandedSkaters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(skaterId)) {
        newSet.delete(skaterId);
      } else {
        newSet.add(skaterId);
      }
      return newSet;
    });
  };

  const registeredCount = mySkaters.filter((s) => paidSkaterIds.has(s.id)).length;
  const notRegisteredCount = mySkaters.length - registeredCount;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Compact Page Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrophyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {navLabels.skaters}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your skaters and track their progress
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary metrics row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {mySkaters.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Skaters
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <PaymentIcon sx={{ fontSize: 28, color: 'success.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {registeredCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Registered
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CalendarIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {notRegisteredCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Not Registered
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CoachIcon sx={{ fontSize: 28, color: 'info.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {userRoles.includes('coach') ? myCoachedSkaters.length : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Coached
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Widget: Season and actions */}
      <WidgetCard
        title="Season"
        icon={<CalendarIcon sx={{ color: 'white', fontSize: 28 }} />}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="season-select-label">Season</InputLabel>
            <Select
              labelId="season-select-label"
              label="Season"
              value={selectedSeasonId}
              onChange={(e) => {
                setHasManualSeasonSelection(true);
                setSelectedSeasonId(e.target.value);
              }}
            >
              {seasons.map((season) => (
                <MenuItem key={season.id} value={season.id}>
                  {season.name || season.year || 'Season'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isCoachOnly ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLinkSkaterDialogOpen(true)}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              Link Skater by USFS ID
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSkater}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
            >
              Add Skater
            </Button>
          )}
        </Box>
      </WidgetCard>

      {/* Widget: My Skaters */}
      <WidgetCard
        title="My Skaters"
        icon={<PersonIcon sx={{ color: 'white', fontSize: 28 }} />}
        sx={{ mb: 3 }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {selectedSeasonId && paymentConfigMissing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Payment processing has not been configured for {selectedSeasonName || 'this season'}.
            Skaters will be marked as unregistered until the interclub enables payments.
          </Alert>
        )}
        {selectedSeasonId === currentSeasonId && !isSeasonRegistrationOpen(selectedSeason) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Season registration is closed until the next season is set up. You can still add skaters and view history.
          </Alert>
        )}
        {showRegistrationPrompt && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/registration')}>
                Register Now
              </Button>
            }
          >
            Season registration is open. Register skaters to activate scores for {selectedSeasonName || 'this season'}.
          </Alert>
        )}

        {showPrePrelimAckNotice && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Pre-Preliminary acknowledgment required
            </Typography>
            <Typography variant="body2">
              {prePrelimNeedsAck.length === 1 ? (
                <>
                  <strong>{prePrelimNeedsAck[0].firstName} {prePrelimNeedsAck[0].lastName}</strong> is at Pre-Preliminary
                  level. You must complete the eligibility acknowledgment before season registration. Click the skater
                  card to expand, then &quot;Edit Skater&quot; and check the Pre-Preliminary acknowledgment box, then save.
                </>
              ) : (
                <>
                  {prePrelimNeedsAck.length} of your skaters are at Pre-Preliminary level and need the eligibility
                  acknowledgment. Edit each skater (expand the card → Edit Skater), check the Pre-Preliminary
                  acknowledgment box, and save. This is required before you can register for the season.
                </>
              )}
            </Typography>
          </Alert>
        )}

        {/* Only show empty state if no skaters AND no coached skaters */}
        {mySkaters.length === 0 && myCoachedSkaters.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.04) 100%)',
          }}
        >
          <Box sx={{
            display: 'inline-flex',
            p: 2.5,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mb: 3,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
          }}>
            <PersonIcon sx={{ fontSize: 48, color: 'white' }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            {isCoachOnly ? 'No Skaters Linked Yet' : 'No Skaters Yet'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
            {isCoachOnly
              ? 'Link existing skaters to your account using their USFS membership ID. Skaters will also appear here automatically when their parents select you as their coach.'
              : 'Start by adding your first skater to track their progress and competition history.'
            }
          </Typography>
          {isCoachOnly ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setLinkSkaterDialogOpen(true)}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Link Skater by USFS ID
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddSkater}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Add Your First Skater
            </Button>
          )}
        </Paper>
      ) : mySkaters.length === 0 ? null : (
        <Grid container spacing={2}>
          {mySkaters.map((skater, index) => {
            const isExpanded = expandedSkaters.has(skater.id);
            const seasonLevels = getSeasonLevels(skater);
            const isRegistered = paidSkaterIds.has(skater.id);

            // Check paused/disqualified/withdrawn status for current season
            const isPausedForSeason = selectedSeasonId && (skater.pausedSeasons || []).includes(selectedSeasonId);
            const isDisqualifiedForSeason = selectedSeasonId && (skater.disqualifiedSeasons || []).includes(selectedSeasonId);
            const isWithdrawnForSeason = selectedSeasonId && (skater.withdrawnSeasons || []).includes(selectedSeasonId);
            const hasEligibilityWarning = isPausedForSeason || isDisqualifiedForSeason || isWithdrawnForSeason;

            return (
              <Grid item xs={12} key={skater.id || index}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: hasEligibilityWarning
                      ? (isDisqualifiedForSeason ? 'error.main' : 'warning.main')
                      : 'grey.200',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: hasEligibilityWarning
                        ? (isDisqualifiedForSeason ? 'error.dark' : 'warning.dark')
                        : 'primary.main',
                      boxShadow: 2,
                    }
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2.5,
                      cursor: 'pointer',
                      bgcolor: isExpanded ? 'primary.50' : 'transparent',
                    }}
                    onClick={() => toggleSkaterExpanded(skater.id)}
                  >
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        mr: 2
                      }}
                    >
                      {skater.firstName?.[0]}{skater.lastName?.[0]}
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {skater.firstName} {skater.lastName}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {registrationsLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <Chip
                              icon={isRegistered ? <PaymentIcon /> : <CalendarIcon />}
                              label={isRegistered ? 'Registered' : 'Not Registered'}
                              color={isRegistered ? 'success' : 'warning'}
                              size="small"
                              variant={isRegistered ? 'filled' : 'outlined'}
                            />
                            {isRegistered && (
                              <Chip
                                icon={<TrophyIcon sx={{ fontSize: '16px !important' }} />}
                                label="Track Eligibility"
                                size="small"
                                color="primary"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/skaters/${skater.id}`);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: 'primary.50',
                                    borderColor: 'primary.main',
                                  }
                                }}
                              />
                            )}
                          </>
                        )}

                        <Chip
                          label={(() => {
                            if (seasonLevels.levelChanged && seasonLevels.startingLevel && seasonLevels.currentLevel && seasonLevels.startingLevel !== seasonLevels.currentLevel) {
                              return `${seasonLevels.startingLevel} → ${seasonLevels.currentLevel}`;
                            }
                            return seasonLevels.currentLevel || seasonLevels.startingLevel || skater.level || 'Level not set';
                          })()}
                          size="small"
                          variant="outlined"
                        />

                        {skater.usfsVerified && (
                          <Chip
                            label="USFS Verified"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {skater.usfsData?.membershipExpiration && (
                          <Chip
                            label={`Expires: ${new Date(skater.usfsData.membershipExpiration).toLocaleDateString()}`}
                            size="small"
                            color={new Date(skater.usfsData.membershipExpiration) < new Date() ? 'error' : 'default'}
                            variant="outlined"
                          />
                        )}

                        {/* USFS Membership Status */}
                        {skater.usfsData?.membershipStatus && skater.usfsData.membershipStatus !== 'unknown' && (
                          <Chip
                            label={skater.usfsData.membershipExpiration
                              ? `USFS ${skater.usfsData.membershipStatus === 'active' || skater.usfsData.membershipStatus === 'Active' ? 'Active' : 'Expired'} — ${new Date(skater.usfsData.membershipExpiration).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                              : `USFS ${skater.usfsData.membershipStatus.charAt(0).toUpperCase() + skater.usfsData.membershipStatus.slice(1)}`
                            }
                            size="small"
                            color={
                              skater.usfsData.membershipStatus === 'active' || skater.usfsData.membershipStatus === 'Active' || skater.usfsData.membershipStatus === 'verified'
                                ? (skater.usfsData.membershipExpiration && new Date(skater.usfsData.membershipExpiration) < new Date() ? 'error' : 'success')
                                : 'error'
                            }
                            variant="outlined"
                          />
                        )}

                        {/* Warning chips for paused/disqualified/withdrawn */}
                        {isDisqualifiedForSeason && (
                          <Chip
                            icon={<BlockIcon sx={{ fontSize: '16px !important' }} />}
                            label="Disqualified"
                            size="small"
                            color="error"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {isPausedForSeason && !isDisqualifiedForSeason && (
                          <Chip
                            icon={<PauseIcon sx={{ fontSize: '16px !important' }} />}
                            label="Paused"
                            size="small"
                            color="warning"
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                        {isWithdrawnForSeason && !isDisqualifiedForSeason && !isPausedForSeason && (
                          <Chip
                            icon={<WithdrawIcon sx={{ fontSize: '16px !important' }} />}
                            label="Withdrawn"
                            size="small"
                            color="default"
                            sx={{ fontWeight: 600, bgcolor: 'grey.200' }}
                          />
                        )}

                        {skater.club && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {skater.club}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {!registrationsLoading && !isRegistered && canRegisterSeason && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/registration');
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 999,
                            display: { xs: 'none', md: 'flex' }
                          }}
                        >
                          Register Now
                        </Button>
                      )}

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSkater(skater);
                        }}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Expandable Content - Simplified Highlights */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider />

                    <Box sx={{ p: 3 }}>
                      {/* Quick Info Grid */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            USFS ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {skater.membershipId || 'Not provided'}
                          </Typography>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            Club
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {skater.club || 'Not specified'}
                          </Typography>
                        </Grid>

                        {(skater.coaches?.length > 0 || skater.coachName) && (
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              Coach
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {skater.coaches?.length > 0
                                ? skater.coaches[0]?.displayName || skater.coaches[0]?.name || 'Unknown'
                                : skater.coachName}
                              {skater.coaches?.length > 1 && ` +${skater.coaches.length - 1}`}
                            </Typography>
                          </Grid>
                        )}

                        {skater.usfsVerified && (
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                              USFS Status
                            </Typography>
                            <Chip
                              label="Verified"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          </Grid>
                        )}
                      </Grid>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={() => navigate(`/skaters/${skater.id}`)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 3,
                            px: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                            }
                          }}
                        >
                          View Details & Eligibility
                        </Button>

                        {!registrationsLoading && !isRegistered && (
                          <Button
                            variant="outlined"
                            onClick={() => navigate('/registration')}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 3,
                            }}
                          >
                            Register for Season
                          </Button>
                        )}

                        <IconButton
                          color="error"
                          onClick={() => handleDeleteSkater(skater.id)}
                          sx={{ ml: 'auto' }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Collapse>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        )}
      </WidgetCard>

      {/* Widget: Coached Skaters */}
      {isCoach && (
        <WidgetCard
          title="Coached Skaters"
          icon={<CoachIcon sx={{ color: 'white', fontSize: 28 }} />}
          sx={{ mt: 3 }}
        >
          {myCoachedSkaters.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary">
                No coached skaters assigned yet.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {myCoachedSkaters.map((skater, index) => (
                <Grid item xs={12} sm={6} md={4} key={skater.id || index}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'primary.main',
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            mr: 2
                          }}
                        >
                          {skater.firstName?.[0] || 'S'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {skater.firstName} {skater.lastName}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={(skater.paid ?? paidSkaterIds.has(skater.id)) ? 'Paid' : 'Unpaid'}
                              color={(skater.paid ?? paidSkaterIds.has(skater.id)) ? 'success' : 'warning'}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {(() => {
                              const seasonLevels = getSeasonLevels(skater);
                              if (
                                seasonLevels.levelChanged
                                && seasonLevels.startingLevel
                                && seasonLevels.currentLevel
                                && seasonLevels.startingLevel !== seasonLevels.currentLevel
                              ) {
                                return `Level: ${seasonLevels.startingLevel} → ${seasonLevels.currentLevel}`;
                              }
                              return `Level: ${seasonLevels.currentLevel || seasonLevels.startingLevel || skater.level || 'Not specified'}`;
                            })()}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Club
                        </Typography>
                        <Typography variant="body1">
                          {skater.club || 'Not specified'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          USFS ID
                        </Typography>
                        <Typography variant="body1">
                          {skater.membershipId || 'Not provided'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewResults(skater)}
                          disabled={!skater.membershipId && !skater.id}
                        >
                          View Results
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </WidgetCard>
      )}

      {/* Widget: Followed Skaters - For Coaches */}
      {userRoles.includes('coach') && (
        <WidgetCard
          title="Followed Skaters"
          icon={<FollowIcon sx={{ color: 'white', fontSize: 28 }} />}
          sx={{ mt: 3 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            {approvalStatus.coach === 'approved' ? (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setFollowSkaterDialogOpen(true)}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600 }}
              >
                Follow Skater
              </Button>
            ) : (
              <Chip label="Pending Approval" color="warning" variant="outlined" />
            )}
          </Box>

          {approvalStatus.coach !== 'approved' ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Your coach role is pending approval from an interclub administrator.
              Once approved, you'll be able to follow skaters by their USFS membership ID.
            </Alert>
          ) : followedSkaters.length === 0 ? (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">
                You're not following any skaters yet. Click "Follow Skater" to add skaters by their USFS membership ID.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {followedSkaters.map((skater) => (
                <Grid item xs={12} sm={6} md={4} key={skater.id}>
                  <Card elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {skater.firstName?.[0]}
                        {skater.lastName?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {skater.firstName} {skater.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {skater.club || 'No club'} • {skater.currentLevel || 'No level'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          USFS: {skater.membershipId || 'N/A'}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleUnfollowSkater(skater.id, `${skater.firstName} ${skater.lastName}`)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </WidgetCard>
      )}

      {/* Widget: Club Skaters - For Club Admins */}
      {userRoles.includes('club_admin') && (
        <WidgetCard
          title="Club Skaters"
          icon={<ClubIcon sx={{ color: 'white', fontSize: 28 }} />}
          sx={{ mt: 3 }}
        >
          {approvalStatus.clubAdmin !== 'approved' ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Your club admin role is pending approval from an interclub administrator.
              Once approved, you'll be able to view all skaters in your club.
            </Alert>
          ) : clubSkaters.length === 0 ? (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">No skaters found in your club.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {clubSkaters.map((skater) => (
                <Grid item xs={12} sm={6} md={4} key={skater.id}>
                  <Card elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.main' }}>
                        {skater.firstName?.[0]}
                        {skater.lastName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {skater.firstName} {skater.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {skater.currentLevel || 'No level'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          USFS: {skater.membershipId || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </WidgetCard>
      )}

      {/* Widget: All Interclub Skaters - For Interclub Admins */}
      {userRoles.includes('interclub_admin') && (
        <WidgetCard
          title="All Interclub Skaters"
          icon={<InterclubIcon sx={{ color: 'white', fontSize: 28 }} />}
          sx={{ mt: 3 }}
        >
          {approvalStatus.interclubAdmin !== 'approved' ? (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Your interclub admin role is pending approval.
              Once approved, you'll be able to view all skaters in your interclub series.
            </Alert>
          ) : interclubSkaters.length === 0 ? (
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, bgcolor: 'grey.50' }}>
              <Typography color="text.secondary">No skaters found in your interclub series.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {interclubSkaters.map((skater) => (
                <Grid item xs={12} sm={6} md={4} key={skater.id}>
                  <Card elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        {skater.firstName?.[0]}
                        {skater.lastName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {skater.firstName} {skater.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {skater.club || 'No club'} • {skater.currentLevel || 'No level'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          USFS: {skater.membershipId || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </WidgetCard>
      )}

      {/* Results Dialog */}
      <Dialog
        open={scoreData !== null || scoreLoading || !!scoreError}
        onClose={() => { setScoreData(null); setScoreError(null); setScoreLoading(false); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrophyIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedSkater?.firstName} {selectedSkater?.lastName}
                </Typography>
                {selectedSeasonName && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedSeasonName} Results
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={() => { setScoreData(null); setScoreError(null); setScoreLoading(false); }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {scoreLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {scoreError && <Alert severity="error" sx={{ borderRadius: 2 }}>{scoreError}</Alert>}
          {scoreData && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Club</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{scoreData.skatingClub}</Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Starting Level</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {(() => {
                      const seasonLevels = getSeasonLevels(selectedSkater || {});
                      return scoreData.startingCompetitionLevel || seasonLevels.startingLevel || 'Not Set';
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Current Level</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {(() => {
                      const seasonLevels = getSeasonLevels(selectedSkater || {});
                      return scoreData.competitionLevel || seasonLevels.currentLevel || 'Not Set';
                    })()}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Competition Points
              </Typography>
              {scoreData.competitions.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No competition results recorded for this season.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {scoreData.competitions.map((comp, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <Typography variant="body2">{comp.competitionName}</Typography>
                      <Chip label={`${comp.points} pts`} size="small" color="primary" />
                    </Paper>
                  ))}
                </Box>
              )}

              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  textAlign: 'center'
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Score</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {calculateTotalScore(scoreData)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Top 3 scores used for qualification
                </Typography>
              </Paper>

              {showQualifierLink && (() => {
                const qualStatus = getQualificationStatus(scoreData);
                if (!qualStatus) return null;
                return (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: qualStatus.bgColor,
                      border: 1,
                      borderColor: qualStatus.color,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: qualStatus.color }}>
                      {qualStatus.message}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => { setScoreData(null); setScoreError(null); setScoreLoading(false); }}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Skater Dialog */}
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSkater ? 'Edit Skater' : 'Add New Skater'}
        </DialogTitle>
        <DialogContent>
          {/* USFS Import Banner - only show for new skaters */}
          {!editingSkater && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'grey.100',
                borderRadius: 2,
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="img"
                  src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                  alt="U.S. Figure Skating"
                  sx={{ height: 32, width: 'auto' }}
                />
                <Typography variant="body2" color="text.secondary">
                  Import skater info from USFS
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setUsfsLinkSkater({
                    id: null,
                    firstName: skaterForm.firstName,
                    lastName: skaterForm.lastName,
                    membershipId: skaterForm.membershipId,
                    isNewSkater: true,
                  });
                  setUsfsLinkError('');
                  setUsfsLinkDialogOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Import
              </Button>
            </Box>
          )}

          <Box sx={{ pt: editingSkater ? 2 : 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={skaterForm.firstName}
                  onChange={(e) => setSkaterForm(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={skaterForm.lastName}
                  onChange={(e) => setSkaterForm(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="USFS Membership ID"
                  value={skaterForm.membershipId}
                  onChange={(e) => setSkaterForm(prev => ({ ...prev, membershipId: e.target.value }))}
                  placeholder="e.g., 1234567"
                  helperText="Required — Enter the skater's USFS member number"
                  required
                  error={!skaterForm.membershipId && skaterForm.firstName && skaterForm.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!skaterForm.level && skaterForm.firstName}>
                  <InputLabel id="level-select-label">Level *</InputLabel>
                  <Select
                    labelId="level-select-label"
                    label="Level *"
                    value={skaterForm.level}
                    onChange={(e) => {
                      const newLevel = e.target.value;
                      setSkaterForm(prev => ({ ...prev, level: newLevel }));
                      // Reset acknowledgment if level changes away from Pre-Preliminary
                      if (newLevel !== 'Pre-Preliminary') {
                        setPrePrelimAcknowledged(false);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Not specified</em>
                    </MenuItem>
                    {levelOptions.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Pre-Preliminary Warning */}
              {skaterForm.level === 'Pre-Preliminary' && (
                <Grid item xs={12}>
                  {/* Show read-only acknowledgment info if already acknowledged */}
                  {editingSkater?.prePrelimAcknowledgedAt ? (
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': { width: '100%' }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Pre-Preliminary Level Acknowledgment on File
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        The Excel Series ineligibility notice was acknowledged on{' '}
                        <strong>
                          {new Date(
                            editingSkater.prePrelimAcknowledgedAt._seconds
                              ? editingSkater.prePrelimAcknowledgedAt._seconds * 1000
                              : editingSkater.prePrelimAcknowledgedAt
                          ).toLocaleString()}
                        </strong>
                        {editingSkater.prePrelimAcknowledgedByEmail && (
                          <> by <strong>{editingSkater.prePrelimAcknowledgedByEmail}</strong></>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pre-Preliminary skaters who compete in Excel Series are ineligible for the interclub championship series.
                      </Typography>

                      {/* Expandable original acknowledgment text */}
                      <Box sx={{ mt: 2 }}>
                        <Button
                          size="small"
                          onClick={() => setPrePrelimAcknowledged(!prePrelimAcknowledged)}
                          endIcon={prePrelimAcknowledged ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ textTransform: 'none', p: 0 }}
                        >
                          {prePrelimAcknowledged ? 'Hide' : 'View'} original acknowledgment
                        </Button>
                        <Collapse in={prePrelimAcknowledged}>
                          <Paper
                            variant="outlined"
                            sx={{
                              mt: 1,
                              p: 2,
                              bgcolor: 'background.paper',
                              borderColor: 'info.light'
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                              <strong> ineligible for our interclub championship series</strong>. Pre-Preliminary skaters who compete in Excel will be disqualified from qualifying for championships.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              If you are unsure about the correct level or competition eligibility, please contact your club administrator before proceeding.
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                              ✓ I understand that Pre-Preliminary skaters who compete in Excel Series will be ineligible for the interclub championship series
                            </Typography>
                          </Paper>
                        </Collapse>
                      </Box>
                    </Alert>
                  ) : (
                    <Alert
                      severity="warning"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-message': { width: '100%' }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Important: Pre-Preliminary Level Notice
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                        <strong> ineligible for our interclub championship series</strong>. Pre-Preliminary skaters who compete in Excel will be disqualified from qualifying for championships.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        If you are unsure about the correct level or competition eligibility, please contact your club administrator before proceeding.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={prePrelimAcknowledged}
                            onChange={(e) => setPrePrelimAcknowledged(e.target.checked)}
                            color="warning"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            I understand that Pre-Preliminary skaters who compete in Excel Series will be ineligible for the interclub championship series
                          </Typography>
                        }
                      />
                    </Alert>
                  )}
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Birth Date"
                  type="date"
                  value={skaterForm.birthDate}
                  onChange={(e) => setSkaterForm(prev => ({ ...prev, birthDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!skaterForm.birthDate && skaterForm.firstName}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!skaterForm.interclubId && skaterForm.firstName}>
                  <InputLabel id="interclub-select-label">Interclub *</InputLabel>
                  <Select
                    labelId="interclub-select-label"
                    label="Interclub *"
                    value={skaterForm.interclubId}
                    onChange={handleInterclubChange}
                  >
                    {interclubs.map((interclub) => (
                      <MenuItem key={interclub.id} value={interclub.id}>
                        {interclub.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!skaterForm.interclubId} required error={!skaterForm.clubId && skaterForm.interclubId}>
                  <InputLabel id="club-select-label">Club *</InputLabel>
                  <Select
                    labelId="club-select-label"
                    label="Club *"
                    value={skaterForm.clubId}
                    onChange={handleClubChange}
                  >
                    {clubs.map((club) => (
                      <MenuItem key={club.id} value={club.id} sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {club.name}
                          </Typography>
                          {club.aliases?.length ? (
                            <Typography variant="caption" color="text.secondary">
                              Includes: {club.aliases.join(', ')}
                            </Typography>
                          ) : null}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={coaches}
                  value={skaterForm.coaches}
                  getOptionLabel={(option) => {
                    const name = option.displayName || option.email || 'Unknown';
                    const usfs = option.usfsMemberId ? ` USFS# ${option.usfsMemberId}` : '';
                    return option.email ? `${name} (${option.email})${usfs}` : `${name}${usfs}`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = option.uid || option.id || option.coachId;
                    const valueId = value.uid || value.id || value.coachId;
                    if (optionId && valueId) return optionId === valueId;
                    return (option.displayName || '') === (value.displayName || '');
                  }}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) => {
                      const name = (option.displayName || '').toLowerCase();
                      const email = (option.email || '').toLowerCase();
                      const usfs = (option.usfsMemberId || '').toString();
                      const q = inputValue.toLowerCase();
                      return name.includes(q) || email.includes(q) || usfs.includes(q);
                    })
                  }
                  onChange={(event, newValue) => {
                    const processedCoaches = newValue.map((item) => ({
                      coachId: item.uid || item.id || item.coachId || '',
                      displayName: item.displayName || item.email || '',
                      email: item.email || '',
                    }));
                    setSkaterForm((prev) => ({ ...prev, coaches: processedCoaches }));
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.email ? `${option.displayName || option.email} (${option.email})` : (option.displayName || 'Unknown')}
                        {...getTagProps({ index })}
                        key={option.coachId || option.displayName || index}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Coach(es) *"
                      placeholder={skaterForm.coaches.length === 0 ? "Search USFS or approved coaches…" : ""}
                      helperText="Select from USFS coach directory or approved coaches in your interclub."
                      required
                      error={!skaterForm.coaches?.length && skaterForm.firstName}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.uid || option.id || option.displayName}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.displayName || 'Unknown'}
                        </Typography>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                        )}
                        {option.usfsMemberId && (
                          <Typography variant="caption" color="text.secondary" display="block">USFS# {option.usfsMemberId}</Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={skaterForm.notes}
                  onChange={(e) => setSkaterForm(prev => ({ ...prev, notes: e.target.value }))}
                  multiline
                  rows={3}
                  placeholder="Any additional notes about the skater..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveSkater}
            variant="contained"
            disabled={
              !skaterForm.firstName ||
              !skaterForm.lastName ||
              !skaterForm.membershipId ||
              !skaterForm.birthDate ||
              !skaterForm.level ||
              !skaterForm.interclubId ||
              !skaterForm.clubId ||
              !skaterForm.coaches?.length ||
              (skaterForm.level === 'Pre-Preliminary' && !prePrelimAcknowledged && !editingSkater?.prePrelimAcknowledgedAt)
            }
          >
            {editingSkater ? 'Update Skater' : 'Add Skater'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Follow Skater Dialog - For Coaches */}
      <Dialog
        open={followSkaterDialogOpen}
        onClose={() => {
          setFollowSkaterDialogOpen(false);
          setFollowSkaterInput('');
          setFollowSkaterError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Follow a Skater</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the skater's USFS membership ID to add them to your followed list.
              You'll be able to view their profile and competition results.
            </Typography>
            {followSkaterError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {followSkaterError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="USFS Membership ID"
              value={followSkaterInput}
              onChange={(e) => setFollowSkaterInput(e.target.value)}
              placeholder="Enter membership ID..."
              disabled={followSkaterLoading}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFollowSkaterDialogOpen(false);
              setFollowSkaterInput('');
              setFollowSkaterError('');
            }}
            color="inherit"
            disabled={followSkaterLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFollowSkater}
            variant="contained"
            disabled={!followSkaterInput.trim() || followSkaterLoading}
            startIcon={followSkaterLoading ? <CircularProgress size={20} /> : null}
          >
            Follow Skater
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Skater Dialog - For Coaches to add existing skaters */}
      <Dialog
        open={linkSkaterDialogOpen}
        onClose={() => {
          setLinkSkaterDialogOpen(false);
          setLinkSkaterInput('');
          setLinkSkaterError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Existing Skater</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              As a coach, you can link to existing skaters who are already registered in the system.
              If the skater hasn't been registered yet, ask their parent to create their profile first.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the skater's USFS membership ID to add them to your coached skaters.
              This will allow you to view their competition results and track their progress.
            </Typography>
            {linkSkaterError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {linkSkaterError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="USFS Membership ID"
              value={linkSkaterInput}
              onChange={(e) => setLinkSkaterInput(e.target.value)}
              placeholder="e.g., 1234567"
              disabled={linkSkaterLoading}
              autoFocus
              helperText="The skater must already be registered by their parent/guardian"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLinkSkaterDialogOpen(false);
              setLinkSkaterInput('');
              setLinkSkaterError('');
            }}
            color="inherit"
            disabled={linkSkaterLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkSkater}
            variant="contained"
            disabled={!linkSkaterInput.trim() || linkSkaterLoading}
            startIcon={linkSkaterLoading ? <CircularProgress size={20} /> : null}
          >
            Link Skater
          </Button>
        </DialogActions>
      </Dialog>

      {/* USFS Link Prompt Dialog - Shown after adding a new skater */}
      <Dialog
        open={usfsLinkPromptOpen}
        onClose={handleUsfsLinkPromptSkip}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
              }}
            >
              <VerifiedIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Link USFS Account?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verify {usfsLinkSkater?.firstName}'s membership
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Would you like to link <strong>{usfsLinkSkater?.firstName}'s</strong> USFS account now?
            </Typography>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Benefits of linking:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>Verify active USFS membership status</li>
                <li>Show membership expiration date</li>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleUsfsLinkPromptSkip}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Skip for Now
          </Button>
          <Button
            onClick={handleUsfsLinkPromptYes}
            variant="contained"
            startIcon={<VerifiedIcon />}
            sx={{ borderRadius: 2 }}
          >
            Link USFS Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* USFS Link Dialog */}
      <USFSLinkDialog
        open={usfsLinkDialogOpen}
        onClose={handleUsfsLinkDialogClose}
        onSubmit={handleUsfsLinkSubmit}
        loading={usfsLinkLoading}
        error={usfsLinkError}
        skaterName={usfsLinkSkater?.firstName}
        initialUsername={usfsLinkSkater?.membershipId || ''}
      />
    </Box>
  );
}
